import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "@mui/x-date-pickers-pro";
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LineChart } from "@mui/x-charts/LineChart";
import axiosInstance from "../AxiosInstance";
import CustomDatePicker from "./CustomDatePicker";
import EposTabs from "./EposTabs";
import ProductFilters from "./ProductFilters";
import ProductFiltersButton from "./ProductFiltersButton";

interface EposPerformanceWeek {
    weekEndingDate: string;
    units: number;
    revenue: number;
    storesSelling: number;
    storeStock: number;
    ros: number | null;
    poundRos: number | null;
    asp: number | null;
}

interface EposPerformanceResponse {
    weeks: EposPerformanceWeek[];
}

interface ProductOption {
    productId: number;
    skuNumber: string;
    skuDescription: string;
    brand: string;
    groupName?: string | null;
}

interface CurrentOrganisation {
    startDayOfWeek: number;
}

type PerformanceMetricKey = "units" | "revenue" | "ros" | "poundRos" | "asp" | "storesSelling" | "storeStock";
type MetricKey = PerformanceMetricKey | "none";

interface MetricDefinition {
    key: PerformanceMetricKey;
    label: string;
}

const metricDefinitions: MetricDefinition[] = [
    { key: "units", label: "Units" },
    { key: "revenue", label: "Revenue" },
    { key: "ros", label: "ROS" },
    { key: "poundRos", label: "£ ROS" },
    { key: "asp", label: "ASP" },
    { key: "storesSelling", label: "Stores selling" },
    { key: "storeStock", label: "Store stock" }
];

const formatNumber = (value: number | null, maximumFractionDigits = 0) =>
    value === null
        ? "—"
        : new Intl.NumberFormat("en-GB", { maximumFractionDigits }).format(value);

const formatCurrency = (value: number | null, maximumFractionDigits = 0) =>
    value === null
        ? "—"
        : new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            maximumFractionDigits
        }).format(value);

const formatWeek = (date: string) => dayjs(date).format("D MMM");

const metricValue = (week: EposPerformanceWeek, key: MetricKey) => key === "none" ? null : week[key];

const formatMetricValue = (week: EposPerformanceWeek, key: PerformanceMetricKey) => {
    const value = metricValue(week, key);

    if (key === "revenue" || key === "poundRos" || key === "asp") {
        return formatCurrency(value, key === "revenue" ? 0 : 2);
    }

    return formatNumber(value, key === "ros" ? 1 : 0);
};

const errorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; detail?: string } | undefined;
        return data?.message ?? data?.detail ?? "EPOS performance could not be loaded.";
    }

    return "EPOS performance could not be loaded.";
};

const sum = (weeks: EposPerformanceWeek[], key: "units" | "revenue" | "storesSelling" | "storeStock") =>
    weeks.reduce((total, week) => total + week[key], 0);

const notNull = <T,>(value: T | null): value is T => value !== null;

const getDefaultDateRange = (startDayOfWeek: number): DateRange<Dayjs> => {
    const normalizedStartDayOfWeek = startDayOfWeek === 7 ? 0 : startDayOfWeek;
    const currentDate = dayjs().startOf("day");
    const currentWeekStart = currentDate.subtract((currentDate.day() - normalizedStartDayOfWeek + 7) % 7, "day");

    return [
        currentWeekStart.subtract(52, "week"),
        currentWeekStart.subtract(1, "day")
    ];
};

export default function EposDataPage() {
    const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
    const [selectedRetailer, setSelectedRetailer] = useState("1");
    const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState<string[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [performance, setPerformance] = useState<EposPerformanceResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [metricOne, setMetricOne] = useState<MetricKey>("units");
    const [metricTwo, setMetricTwo] = useState<MetricKey>("revenue");
    const [unmappedProductCount, setUnmappedProductCount] = useState(0);

    useEffect(() => {
        let cancelled = false;

        const loadUnmappedProductCount = async () => {
            try {
                const response = await axiosInstance.get<{ count: number }>("/epos/product-mappings/unmapped-count", {
                    params: { retailer: selectedRetailer }
                });

                if (!cancelled) {
                    setUnmappedProductCount(response.data.count);
                }
            } catch {
                if (!cancelled) {
                    setUnmappedProductCount(0);
                }
            }
        };

        void loadUnmappedProductCount();

        return () => {
            cancelled = true;
        };
    }, [selectedRetailer]);

    useEffect(() => {
        const loadOrganisation = async () => {
            try {
                const { data } = await axiosInstance.get<CurrentOrganisation>("/organisation/current");
                setDateRange(currentDateRange =>
                    currentDateRange[0] && currentDateRange[1]
                        ? currentDateRange
                        : getDefaultDateRange(data.startDayOfWeek)
                );
            } catch {
                setDateRange(currentDateRange =>
                    currentDateRange[0] && currentDateRange[1]
                        ? currentDateRange
                        : getDefaultDateRange(0)
                );
            }
        };

        void loadOrganisation();
    }, []);

    const hasProductFilters = selectedBrands.length > 0 || selectedProductGroups.length > 0 || selectedProductIds.length > 0;

    const filteredProductIds = useMemo(() => {
        if (!hasProductFilters) {
            return undefined;
        }

        return productOptions
            .filter(product =>
                (selectedBrands.length === 0 || selectedBrands.includes(product.brand)) &&
                (selectedProductGroups.length === 0 || selectedProductGroups.includes(product.groupName ?? "")) &&
                (selectedProductIds.length === 0 || selectedProductIds.includes(product.productId)))
            .map(product => product.productId);
    }, [hasProductFilters, productOptions, selectedBrands, selectedProductGroups, selectedProductIds]);

    const load = useCallback(async () => {
        const fromDate = dateRange[0];
        const toDate = dateRange[1];

        if (!fromDate || !toDate) {
            return;
        }

        if (filteredProductIds && filteredProductIds.length === 0) {
            setPerformance({ weeks: [] });
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");

        try {
            const params: {
                retailer: string;
                fromDate?: string;
                toDate?: string;
                productIds?: number[];
            } = { retailer: selectedRetailer };

            params.fromDate = fromDate.format("YYYY-MM-DD");
            params.toDate = toDate.format("YYYY-MM-DD");

            if (filteredProductIds) {
                params.productIds = filteredProductIds;
            }

            const response = await axiosInstance.get<EposPerformanceResponse>("/epos/performance", {
                params,
                paramsSerializer: { indexes: null }
            });
            setPerformance(response.data);
        } catch (requestError) {
            setError(errorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, [dateRange, filteredProductIds, selectedRetailer]);

    useEffect(() => {
        void load();
    }, [load]);

    const weeks = useMemo(() => performance?.weeks ?? [], [performance]);
    const latestWeek = weeks.length > 0 ? weeks[weeks.length - 1] : null;

    const summary = useMemo(() => {
        const units = sum(weeks, "units");
        const revenue = sum(weeks, "revenue");
        const storesSelling = sum(weeks, "storesSelling");

        return {
            units,
            revenue,
            ros: storesSelling === 0 ? null : units / storesSelling,
            poundRos: storesSelling === 0 ? null : revenue / storesSelling,
            asp: units === 0 ? null : revenue / units
        };
    }, [weeks]);

    const weeklyPerformanceTable = useMemo(() => {
        const headers = ["Metric", ...weeks.map(week => formatWeek(week.weekEndingDate))];
        const rows = metricDefinitions.map(metric => ([
            metric.label,
            ...weeks.map(week => formatMetricValue(week, metric.key))
        ]));

        return { headers, rows };
    }, [weeks]);

    const chartSeries = [
        metricOne === "none" ? null : {
            label: metricDefinitions.find(metric => metric.key === metricOne)?.label ?? metricOne,
            data: weeks.map(week => metricValue(week, metricOne))
        },
        metricTwo === "none" ? null : {
            label: metricDefinitions.find(metric => metric.key === metricTwo)?.label ?? metricTwo,
            data: weeks.map(week => metricValue(week, metricTwo))
        }
    ].filter(notNull);

    const exportCsv = () => {
        if (weeks.length === 0) {
            return;
        }

        const csv = [weeklyPerformanceTable.headers, ...weeklyPerformanceTable.rows]
            .map(row => row.map(value => '"' + String(value).replace(/"/g, '""') + '"').join(","))
            .join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "epos-performance.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const copyWeeklyPerformanceToClipboard = async () => {
        if (weeks.length === 0) {
            return;
        }

        const text = [weeklyPerformanceTable.headers, ...weeklyPerformanceTable.rows]
            .map(row => row.map(value => String(value)).join("\t"))
            .join("\n");

        try {
            await navigator.clipboard.writeText(text);
        } catch (clipboardError) {
            console.error("Failed to copy weekly performance to clipboard:", clipboardError);
        }
    };

    const renderMetricSelect = (
        label: string,
        value: MetricKey,
        onChange: (nextValue: MetricKey) => void,
        allowNone = false
    ) => (
        <FormControl size="small" sx={{ minWidth: 142 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                label={label}
                value={value}
                onChange={event => onChange(event.target.value as MetricKey)}
            >
                {(allowNone ? [{ key: "none" as const, label: "None" }, ...metricDefinitions] : metricDefinitions).map(metric => (
                    <MenuItem key={metric.key} value={metric.key}>{metric.label}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    return (
        <Box sx={{ width: "100%", maxWidth: 1500, minWidth: 0, mx: "auto", py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
            <EposTabs unmappedCount={unmappedProductCount} />

            {unmappedProductCount > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                >
                    {unmappedProductCount === 1
                        ? "Product filters cannot be applied to 1 unlinked product."
                        : `Product filters cannot be applied to ${unmappedProductCount} unlinked products.`}
                </Alert>
            )}

            <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-end"
                    sx={{
                        "& .MuiStack-root": {
                            overflow: "visible",
                            paddingTop: 0
                        }
                    }}
                >
                    <Grid item xs={12} lg={5} sx={{ display: "flex", alignItems: "flex-end" }}>
                        <Box sx={{ width: "100%" }}>
                            <CustomDatePicker
                                value={dateRange}
                                onChange={setDateRange}
                                startLabel="From"
                                endLabel="To"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", alignItems: "flex-end" }}>
                        <FormControl fullWidth>
                            <InputLabel>Retailer</InputLabel>
                            <Select
                                label="Retailer"
                                value={selectedRetailer}
                                onChange={event => setSelectedRetailer(event.target.value)}
                            >
                                <MenuItem value="1">Sainsbury&apos;s</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}>
                        <ProductFiltersButton
                            filtersExpanded={filtersExpanded}
                            onToggle={() => setFiltersExpanded(expanded => !expanded)}
                        />
                    </Grid>
                </Grid>
            </Card>

            <ProductFilters
                selectedForecastId={null}
                selectedForecastPeriodId={null}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedProductGroups={selectedProductGroups}
                setSelectedProductGroups={setSelectedProductGroups}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                filtersExpanded={filtersExpanded}
                onToggle={() => setFiltersExpanded(expanded => !expanded)}
                selectedCustomerId={null}
                selectedChannelId={null}
                useOrganisationProducts
                onProductsLoaded={setProductOptions}
            />

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? (
                <Card variant="outlined" sx={{ width: "100%", minWidth: { xs: 0, sm: 600 } }}>
                    <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                        <CircularProgress />
                    </Box>
                </Card>
            ) : weeks.length === 0 ? (
                <Card variant="outlined" sx={{ width: "100%", minWidth: { xs: 0, sm: 600 } }}>
                    <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
                        <Typography variant="h6">No EPOS data available</Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            Try changing the date range or product filters.
                        </Typography>
                    </Box>
                </Card>
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="Units" value={formatNumber(summary.units)} detail="Selected period" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="Revenue" value={formatCurrency(summary.revenue)} detail="Selected period" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="ROS" value={formatNumber(summary.ros, 1)} detail="Units/store/week" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="£ ROS" value={formatCurrency(summary.poundRos, 2)} detail="Per store/week" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="ASP" value={formatCurrency(summary.asp, 2)} detail="Revenue per unit" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="Stores selling" value={formatNumber(latestWeek?.storesSelling ?? null)} detail="Latest week" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MetricCard label="Store stock" value={formatNumber(latestWeek?.storeStock ?? null)} detail="Latest week" />
                        </Grid>
                    </Grid>

                    <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "flex-end" }}
                            spacing={2}
                            sx={{ mb: 2 }}
                        >
                            <Box>
                                <Typography variant="h6">Performance Trend</Typography>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                {renderMetricSelect("Metric 1", metricOne, setMetricOne)}
                                {renderMetricSelect("Metric 2", metricTwo, setMetricTwo, true)}
                            </Stack>
                        </Stack>
                        <LineChart
                            height={320}
                            xAxis={[{
                                scaleType: "point",
                                data: weeks.map(week => formatWeek(week.weekEndingDate))
                            }]}
                            series={chartSeries}
                            margin={{ left: 70, right: 20, top: 20, bottom: 35 }}
                        />
                    </Card>

                    <Card variant="outlined">
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "center" }}
                            spacing={2}
                            sx={{
                                p: { xs: 2, sm: 2.5 },
                                pb: 2,
                                borderBottom: 1,
                                borderColor: "divider"
                            }}
                        >
                            <Typography variant="h6">Weekly Performance</Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                <Tooltip title="Copy table to clipboard">
                                    <IconButton aria-label="Copy table to clipboard" onClick={copyWeeklyPerformanceToClipboard}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Export CSV">
                                    <IconButton aria-label="Export CSV" onClick={exportCsv}>
                                        <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                        <TableContainer sx={{ overflowX: "auto" }}>
                            <Table
                                stickyHeader
                                size="small"
                                aria-label="weekly performance table"
                                sx={{
                                    minWidth: 760,
                                    width: "max-content",
                                    tableLayout: "auto",
                                    "& .MuiTableBody-root .MuiTableCell-root": {
                                        whiteSpace: "nowrap"
                                    },
                                    "& .MuiTableHead-root .MuiTableCell-root": {
                                        whiteSpace: "nowrap"
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            fontWeight: 700,
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 4,
                                            backgroundColor: theme => theme.palette.background.paper
                                        }}>
                                            Metric
                                        </TableCell>
                                        {weeks.map(week => (
                                            <TableCell key={week.weekEndingDate} align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                                {formatWeek(week.weekEndingDate)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {metricDefinitions.map(metric => (
                                        <TableRow key={metric.key}>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                sx={{
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor: theme => theme.palette.background.paper
                                                }}
                                            >
                                                {metric.label}
                                            </TableCell>
                                            {weeks.map(week => (
                                                <TableCell key={week.weekEndingDate} align="right" sx={{ whiteSpace: "nowrap" }}>
                                                    {formatMetricValue(week, metric.key)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </>
            )}
        </Box>
    );
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
    return (
        <Card variant="outlined" sx={{ height: "100%", p: 2 }}>
            <Typography color="text.secondary" variant="body2">{label}</Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>{value}</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>{detail}</Typography>
        </Card>
    );
}
